import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-bffc17e6"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "refreshing-indicator"
}
const _hoisted_2 = { class: "indicator-left" }
const _hoisted_3 = { class: "indicator-right" }
const _hoisted_4 = { class: "indicator-quarter-mask" }
const _hoisted_5 = { class: "pull-refresh-container" }

import { ref, computed, reactive, watch, watchPostEffect } from 'vue'
import { throttle } from 'lodash'
import { interceptDigital } from '@remote/utils/base'
/** 下拉刷新状态值: 初始位置 | 下拉中 | 释放可刷新 | 刷新中 | 刷新成功 | 刷新失败 */
type TRefreshStatus = 'init' | 'pulling' | 'ready' | 'refreshing' | 'done' | 'fail'
interface IProps {
  disabled?: boolean
  noScrollbar?: boolean
  /** 某些场景下插槽高度与组件高度一致（即没有滚动条），但插槽内部有自己的滚动条，即滚动元素为插槽内层元素，此时需要将该滚动元素通过此props方法返回 */
  getScrollElement?: () => HTMLElement
  /** 该值变化时可触发重新调用getScrollElement获取滚动元素（自定义滚动元素有变化时使用） */
  scrollKey?: string | number
  width?: number
  height?: number
  indicator?: {
    color?: string
    backgroundColor?: string
  }
}
interface IEmits {
  (e: 'refresh', next: (status: Extract<TRefreshStatus, 'done' | 'fail'>) => void): void
}

export default /*#__PURE__*/_defineComponent({
  props: {
    disabled: { type: Boolean },
    noScrollbar: { type: Boolean },
    getScrollElement: null,
    scrollKey: null,
    width: null,
    height: null,
    indicator: null
  },
  emits: ["refresh"],
  setup(__props: any, { emit: $emit }: { emit: ({
  (e: 'refresh', next: (status: Extract<TRefreshStatus, 'done' | 'fail'>) => void): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  disabled?: boolean
  noScrollbar?: boolean
  /** 某些场景下插槽高度与组件高度一致（即没有滚动条），但插槽内部有自己的滚动条，即滚动元素为插槽内层元素，此时需要将该滚动元素通过此props方法返回 */
  getScrollElement?: () => HTMLElement
  /** 该值变化时可触发重新调用getScrollElement获取滚动元素（自定义滚动元素有变化时使用） */
  scrollKey?: string | number
  width?: number
  height?: number
  indicator?: {
    color?: string
    backgroundColor?: string
  }
}




// function logs (..._args) {
//   console.log('[AndroidPullToRefresh]', ..._args)
// }

const listeners: Function[] = []
function clearListeners () {
  listeners.forEach(unlisten => unlisten())
  listeners.length = 0
}
const scrollElRef = ref<HTMLElement>()
const status = ref<TRefreshStatus>('init')
const refreshing = computed(() => status.value === 'refreshing')
const indicator = computed(() => {
  const dft: IProps['indicator'] = { color: 'red', backgroundColor: 'white' }
  return Object.assign(dft, props.indicator)
})
// 下拉刷新配置
const options = reactive({
  // 添加滚动条到顶部时触发下拉刷新限制，增加用户体验感
  canPull: true,
  // 下拉距离超过指定值，进入释放刷新状态
  triggerPullHeight: 80,
  // 最大可下拉距离
  maxPullHeight: 200,
  // 触发上划加载更多的距离，大于1时为px距离, 0~1为比例
  triggerLoadHeight: 50,
  // 动画持续时间(毫秒)，0就是没有动画
  duration: 0,
  // 下拉刷新指示器放大倍数
  scale: 1,
  // 指示器是否展示箭头
  arrow: false,
  // 指示器透明度
  opacity: 1,
  // 设置顶部缓冲阴影大小 
  topButters: 0,
})

// 记录
const snaps = reactive({
  /** 本次下拉刷新的touch_id */
  tid: '',
  // 保存第一次点击屏幕时的Y坐标
  startY: 0,
  // 设置动画距离顶部的位置
  distance: -35,
  // 记录滚动条位置
  scrollOffset: 0,
  // 转动角度
  rotateAngle: 0,
})

// 限制下拉，防止因滚动条到顶部时立即触发下拉刷新
const canPull = ref(true)
const pullLockTimer = ref(0)

const style = computed(() => {
  const width = props.width ? props.width + 'px' : '100%'
  const height = props.height ? props.height + 'px' : '100%'
  return { width, height }
})
const refreshStyle = computed(() => {
  const transition = options.duration + 'ms'
  const transform = `translateY(${snaps.distance}px) scale(${options.scale})`
  return { transition, transform }
})
const pullingStyle = computed(() => {
  const opacity = options.opacity
  const transition = options.duration + 'ms'
  const borderColor = props.indicator?.color || 'red'
  const transform = `rotate(${100 + snaps.rotateAngle || 0}deg)`

  return {
    borderColor,
    transition,
    transform,
    opacity,
  }
})

function onTouchStart (evt: TouchEvent) {
  if (props.disabled) return
  if(!canPull.value) return
  if (refreshing.value) return
  // 滚动条不达到顶部不触发下拉刷新
  if (snaps.scrollOffset !== 0) return
  // 重置数据配置
  snaps.distance = -35
  options.duration = 0
  options.scale = 0
  options.arrow = false
  snaps.startY = evt.targetTouches[0].clientY
  snaps.tid = Date.now().toString(36)
}

function onTouchEnd () {
  if (props.disabled) return
  if (refreshing.value) return
  snaps.tid = ''
  if (snaps.scrollOffset !== 0) return

  options.duration = 300
  setTimeout(() => {
    // 确保最终topButters为0
    options.topButters = 0
  }, 60)
  if (snaps.distance > options.triggerPullHeight) {
    status.value = 'refreshing'
    const updateStatus = (nextStatus: Extract<TRefreshStatus, 'done' | 'fail'>) => {
      status.value = nextStatus
      setTimeout(() => {
        status.value = 'init'
      }, 800)
    }
    $emit('refresh', updateStatus)
  } else {
    status.value = 'init'
    snaps.distance = -35
  }
}

const onTouchMove = (evt: TouchEvent) => {
  if (props.disabled) return
  if (!canPull.value) return
  if (refreshing.value) return
  // 未经onTouchStart处理的Touch事件不处理
  if (!snaps.tid) return
  if (snaps.scrollOffset !== 0) return
  // 当前移动距离
  const d = evt.targetTouches[0].clientY - snaps.startY
  if (d <= 0) return
  // 仅需处理正常下拉距离范围内
  if (d < options.maxPullHeight) {
    // 下拉旋转度数
    const angle = Math.pow(d, 1.2)
    snaps.rotateAngle = angle
    // 是否展示箭头
    options.arrow = d > 15
    const pullHeight = options.triggerPullHeight
    if (d > pullHeight) {
      // 增加滑动阻力的感觉
      snaps.distance = Math.pow(d, 0.9) - 35
      options.opacity = 1
      options.scale = 1
      status.value = 'ready'
    } else {
      const rate = interceptDigital(d / pullHeight, 2)
      snaps.distance = rate * Math.pow(pullHeight, 0.9) - 35
      options.opacity = rate
      // options.scale = rate
      status.value = 'pulling'
    }
  } else {
    // 确保下拉距离超过范围时能正确进入ready状态
    snaps.distance = Math.pow(options.maxPullHeight, 0.9) - 35
    options.opacity = 1
    options.scale = 1
    status.value = 'ready'
  }
}

const onScroll = throttle((evt: MouseEvent) => {
  const targetEl = evt.target as HTMLDivElement
  const isCustomScroll = targetEl.scrollHeight <= targetEl.offsetHeight
  const scrollEl = isCustomScroll ? (props.getScrollElement?.() || targetEl) : targetEl
  const scrollOffset = scrollEl.scrollTop
  snaps.scrollOffset = scrollOffset

  // TODO: 可能滚动的元素并非targetEl
  // 防止滚动条过快从下到上触发下拉刷新
  if (scrollOffset === 0) {
    // 显示缓冲区域（禁用状态下不展示缓冲区域）
    options.topButters = props.disabled ? 0 : 80
    pullLockTimer.value = window.setTimeout(() => {
      options.topButters = 0
      options.canPull = true
    }, 800)
  } else {
    options.topButters = 0
    options.canPull = false
    if (pullLockTimer.value) {
      window.clearTimeout(pullLockTimer.value)
      pullLockTimer.value = 0
    }
  }

  if (props.disabled) return
  if (status.value === 'refreshing') return
  // TODO: 上拉加载更多
  // 滚动条距离底部的距离
  const bottom = scrollEl.scrollHeight - (scrollEl.scrollTop + scrollEl.clientHeight)
  const triggerHeight = options.triggerLoadHeight > 1 ? options.triggerLoadHeight : Math.floor(options.triggerLoadHeight * scrollEl.clientHeight)
  if (bottom <= triggerHeight) {
    // emit('load')
  }
}, 34, { leading: true, trailing: true })

watch(status, (newVal) => {
  if (newVal === 'init' && options.duration === 300) {
    snaps.distance = -35
  }
})

/** 获取用户自定义的滚动元素 */
function getScrollEl () {
  const targetEl = scrollElRef.value
  if (!targetEl) return
  const isCustomScroll = targetEl.scrollHeight <= targetEl.offsetHeight
  if (!isCustomScroll) return
  const scrollEl = props.getScrollElement?.()
  return scrollEl
}

watchPostEffect(() => {
  const scrollKey = props.scrollKey
  clearListeners()
  const scrollEl = getScrollEl()
  if (!scrollEl) return
  scrollEl.addEventListener('scroll', onScroll)
  const unlisten = () => scrollEl.removeEventListener('scroll', onScroll)
  listeners.push(unlisten)
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["xl-pull-refresh", { 'no-scrollbar': __props.noScrollbar }]),
    style: _normalizeStyle(_unref(style))
  }, [
    _createElementVNode("div", {
      class: "refresh-animation-box",
      style: _normalizeStyle(_unref(refreshStyle))
    }, [
      _createElementVNode("div", {
        class: "circle-indicator",
        style: _normalizeStyle({ backgroundColor: _unref(indicator).backgroundColor })
      }, [
        (_unref(refreshing))
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", {
                  class: "left-circle",
                  style: _normalizeStyle({ backgroundColor: _unref(indicator).color })
                }, null, 4)
              ]),
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", {
                  class: "right-circle",
                  style: _normalizeStyle({ backgroundColor: _unref(indicator).color })
                }, null, 4)
              ]),
              _createElementVNode("div", {
                class: "among-refreshing",
                style: _normalizeStyle({ backgroundColor: _unref(indicator).backgroundColor })
              }, null, 4)
            ]))
          : (_openBlock(), _createElementBlock("div", {
              key: 1,
              class: "pulling-indicator",
              style: _normalizeStyle(_unref(pullingStyle))
            }, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", {
                  class: "indicator-arrow",
                  style: _normalizeStyle({ borderBottomColor: _unref(indicator).color, opacity: _unref(options).arrow ? 1 : 0 })
                }, null, 4)
              ])
            ], 4))
      ], 4)
    ], 4),
    _createElementVNode("div", {
      class: "pull-buffer",
      style: _normalizeStyle({ height: _unref(options).topButters + 'px' })
    }, null, 4),
    _createElementVNode("div", {
      ref_key: "scrollElRef",
      ref: scrollElRef,
      class: "pull-refresh",
      onTouchstartCapture: onTouchStart,
      onTouchmoveCapture: onTouchMove,
      onTouchendCapture: onTouchEnd,
      onScroll: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(onScroll) && _unref(onScroll)(...args)))
    }, [
      _createElementVNode("div", _hoisted_5, [
        _renderSlot(_ctx.$slots, "default")
      ])
    ], 544)
  ], 6))
}
}

})